import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Backbone → React: Handling state with MobX",
  "description": "",
  "published": "2016-09-22T00:00:00.000Z",
  "redirect_from": ["/blog/backbone-%e2%86%92-react-handling-state-with-mobx/swizec/7051"],
  "categories": "backbone, backbonejs, Front End, react, reactjs, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`I did it! ?? I figured out a way to reliably interop between modern unidirectional data flow React components and old Backbone views. It ain’t pretty, but it works.`}</p>
    <p>{`There are three components in that page:`}</p>
    <ul>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`CurrentCount`}</inlineCode>{` component that shows current counter state; it’s pure React`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`ButtonWrapper`}</inlineCode>{` component that shows counter state and does `}<inlineCode parentName="li">{`+1`}</inlineCode>{` on click; it’s a wrapped Backbone view`}</li>
      <li parentName="ul">{`a `}<inlineCode parentName="li">{`ReactButton`}</inlineCode>{` component that does `}<inlineCode parentName="li">{`+10`}</inlineCode>{` on click; it’s `}<em parentName="li">{`also`}</em>{` pure React`}</li>
    </ul>
    <p>{`I know this is a trivial example, but it shows a powerful concept. That `}<inlineCode parentName="p">{`+1`}</inlineCode>{` button is still the same `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/backbone-react-step-1/swizec/7042"
      }}>{`Backbone view from Tuesday`}</a>{`. It stores state in a local Backbone Model, it uses a Handlebars template, and it remains the same idiomatic Backbone View it’s always been. Yet it interops with the React app, blissfully unaware that something fucky’s going on.`}</p>
    <p><img parentName="p" {...{
        "src": "/1afa1fd7a9f47dad475ae1c6ad748077/ZlUUWAf.gif",
        "alt": "The Backbone view"
      }}></img></p>
    <p>{`The Backbone view`}</p>
    <p>{`All three components share the same MobX data store, which has a single observable value called `}<inlineCode parentName="p">{`N`}</inlineCode>{`. It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class CounterStore {
  @observable N = 0;
}
`}</code></pre>
    <p>{`In MobX, stores are classes with observable properties. They often have methods and computed values as well, but this example is too simple.`}</p>
    <p><inlineCode parentName="p">{`@observable`}</inlineCode>{` is a decorator that compiles into something like `}<inlineCode parentName="p">{`makeObservable(this.N, 0)`}</inlineCode>{`, which in turn uses ES6 to add magical getters and setters that fire up the MobX engine whenever you access – dereference – the observable value. Doing it yourself would look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class CounterStore {
  N = 0;

  set N(val) {
    this.N = val;

    // notify all observers that N has changed
  }

  get N() {
    // add call site to list of observers

    return N;
  }
}
`}</code></pre>
    <p>{`MobX saves you from writing that logic yourself, and it adds a bunch of smartness to make it fast and efficient. I don’t really know how the engine works, but after reading the docs and some of Michel Weststrate’s Medium posts, I’m convinced it’s amaze.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Benchmarked Immutables against Observables in TodoMVC. More details at `}<a parentName="p" {...{
          "href": "https://twitter.com/ReactAmsterdam"
        }}>{`@ReactAmsterdam`}</a>{` conf! `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/reactjs?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#reactjs`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/redux?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#redux`}</a>{` `}<a parentName="p" {...{
          "href": "https://twitter.com/hashtag/mobxjs?src=hash&ref_src=twsrc%5Etfw"
        }}>{`#mobxjs`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/tL536DWR6f"
        }}>{`pic.twitter.com/tL536DWR6f`}</a></p>{`— Michel Weststrate (@mweststrate) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/mweststrate/status/718444275239882753"
      }}>{`April 8, 2016`}</a></blockquote>
    <p>{`So that’s the store - no boilerplate involved. The two pure React components don’t involve much boilerplate either.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@inject('counterStore')
class ReactButton extends Component {
    buttonClicked() {
        this.props.counterStore.N += 10;
    }

    render() {
        return (
            <div>
                <p>React Button:</p>
                <button onclick="{action('inc-counter'," this.buttonclicked.bind(this))}="">Jump click count +10</button>
            </div>
        );
    }
};

@inject('counterStore') @observer
class CurrentCount extends Component {
    render() {
        const { N } = this.props.counterStore;
        return (<p>Current count in counterStore: {N}</p>)
    }
}
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`@inject`}</inlineCode>{` decorator takes props from a React context and adds them to a component. I don’t know how MobX-specific this is, but it reduces our boilerplate. Instead of giving each component a `}<inlineCode parentName="p">{`store={this.props.store}`}</inlineCode>{` type of prop, we can wrap the whole thing in a `}<inlineCode parentName="p">{`<Provider>`}</inlineCode>{` and give everyone access.`}</p>
    <p>{`At the end of the day, you `}<em parentName="p">{`always`}</em>{` realize that all your components need access to your application state. Global singletons for things everyone needs make life easier. Trust me.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`@observer`}</inlineCode>{` decorator comes from MobX’s React bindings. It automagically makes the `}<inlineCode parentName="p">{`render()`}</inlineCode>{` method listen to store changes, but only those changes that it uses.`}</p>
    <p><strong parentName="p">{`This is key`}</strong>{`. It’s what makes MobX fast. It’s also what leads to confusion when you’re doing things that are not idiomatic React, like inserting Backbone views into React components.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`This is weird. If I add console.log to the render() method, MobX works as expected. Take it out, and observer isn't updating.`}<a parentName="p" {...{
          "href": "https://twitter.com/mweststrate"
        }}>{`@mweststrate`}</a>{`?`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/778666202457440256"
      }}>{`September 21, 2016`}</a></blockquote>
    <p>{`Thanks to Michel Weststrate for helping me out. His tip about using `}<inlineCode parentName="p">{`autorun`}</inlineCode>{` saved the day ??`}</p>
    <p>{`And with that out of the way, I had a Backbone view wrapped in a React component, including full data interop. Change local Backbone state, and the global data store finds out, changes the data store, and the Backbone view updates.`}</p>
    <p>{`It looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`@inject('counterStore') @observer
class ButtonWrapper extends Component {
    constructor(props) {
        super(props);

        this._init();

        autorun(this._render.bind(this));
    }

    _init() {
        this.button = new BackboneButton({
            N: this.props.counterStore.N
        });
        this.button.model.on('change:N', action('inc-counter', (_, N) => {
            this.props.counterStore.N = N;
        }));
    }

    componentDidUpdate() { this._render(); }
    componentDidMount() { this._render(); }

    _render() {
        this._cleanup();
        this._init();
        this.button.setElement(this.refs.anchor).render();
    }

    componentWillUnmount() { this._cleanup(); }

    _cleanup() {
        this.button.undelegateEvents();
    }

    render() {
        return (
            <div>
                <p>Backbone Button:</p>
                <div class="button-anchor" ref="anchor">
            </div>
        );
    }
}
</div>
`}</code></pre>
    <p>{`Like I said, it ain’t pretty ?`}</p>
    <p>{`It works like this:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`React renders an anchor `}<inlineCode parentName="p">{`div`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`on component mount or update, it runs Backbone view rendering in `}<inlineCode parentName="p">{`_render`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`on component unmount, it cleans up Backbone’s DOM event listeners`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`inside `}<inlineCode parentName="p">{`_render`}</inlineCode>{` it:`}</p>
      </li>
    </ul>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`cleans up Backbone DOM event listeners`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`instantiates a new Backbone view with `}<inlineCode parentName="p">{`_init`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`tells the view to render inside that anchor `}<inlineCode parentName="p">{`<div>`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`when initializing the view in `}<inlineCode parentName="p">{`_init`}</inlineCode>{` it:`}</p>
      </li>
    </ul>
    <ul>
      <li parentName="ul">{`creates a new `}<inlineCode parentName="li">{`BackboneButton`}</inlineCode>{` instance and gives it the `}<em parentName="li">{`value`}</em>{` of `}<inlineCode parentName="li">{`N`}</inlineCode>{` from our store`}</li>
      <li parentName="ul">{`listens for changes to `}<inlineCode parentName="li">{`N`}</inlineCode>{` on the view’s internal state and communicates them upstream with a MobX action`}</li>
    </ul>
    <p>{`This approach is a leaky abstraction. The `}<inlineCode parentName="p">{`_init`}</inlineCode>{` method has to know intimate details about the Backbone view you’re wrapping. There’s no way to get around that because MobX needs those getters and setters to observe state changes.`}</p>
    <p>{`As soon as you pass a value, MobX loses track. I tried passing the whole store into a Backbone view and using it directly as a model, but that didn’t work. Backbone Model’s `}<inlineCode parentName="p">{`set()`}</inlineCode>{` and `}<inlineCode parentName="p">{`get()`}</inlineCode>{` methods circumvent native getters and setters, which means MobX can’t track changes or uses.`}</p>
    <p>{`Another issue is that because `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/backbone-%E2%86%92-react-its-a-people-problem-after-all-%F0%9F%98%91/swizec/7049"
      }}>{`our Backbone views aren’t pure`}</a>{`, the UI could sometimes look wrong to the user. It won’t be stale, but it won’t show all side-effects from user actions either.`}</p>
    <p>{`But we can deal with that later. The important part is that we have a way forward! A way to go from Backbone to React without resorting to a full complete rewrite of everything from scratch. `}{`\\`}{`o/`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      